import { translate } from "app/language/service"
import { shapeEventModule } from "shape/event"
import {
  IGeoShape,
  IGeoShapeCity,
  IGeoShapeCountry,
  IGeoShapeMetropolitan,
  IGeoShapePostalCode,
  IGeoShapeState,
  IGetShapeTitleParams,
  ILookupCity,
  IShapeOnSetParams,
  MAX_POSTAL_CODE_LENGTH,
  SHAPE_EVENTS,
  SHAPE_TYPES
} from "../model"
import { shapeRPO } from "../repository"
import { getMetropolitanWithShape } from "./metropolitan"

export function notifyShapesAdd(newShapes: IGeoShape[]): void {
  shapeEventModule.notify(SHAPE_EVENTS.SHAPES_ADD, newShapes)
}

export function notifyShapesSet(params: IShapeOnSetParams): void {
  shapeEventModule.notify(SHAPE_EVENTS.SHAPES_SET, params)
}

export function notifyShapeDelete(shape: IGeoShape): void {
  shapeEventModule.notify(SHAPE_EVENTS.SHAPE_DELETE, shape)
}

export function notifyShapesDelete(shapes: IGeoShape[]): void {
  shapeEventModule.notify(SHAPE_EVENTS.SHAPES_DELETE, shapes)
}

export function searchCities(filter: string): Promise<IGeoShapeCity[]> {
  return shapeRPO.searchCities(filter)
}

export const getCitiesByNames = (names: string[]): Promise<ILookupCity[]> => shapeRPO.getCitiesByNames(names)

export async function searchPostalCodes(filter: string, addEndStar?: boolean): Promise<IGeoShapePostalCode[]> {
  if (filter.length > MAX_POSTAL_CODE_LENGTH) {
    return Promise.resolve([])
  }
  const postalCodes: IGeoShapePostalCode[] = await shapeRPO.searchPostalCodes(filter)
  if (postalCodes.length > 1) {
    const firstPostalCode = postalCodes[0]
    const postal_code_shape_ids = postalCodes.map(p => p.postal_code_shape_ids).flat()
    postalCodes.unshift({
      ...firstPostalCode,
      id: -1,
      postal_code_shape_ids: postal_code_shape_ids.filter((id, index) => postal_code_shape_ids.indexOf(id) === index),
      postal_code: addEndStar ? `${filter}*` : filter
    })
  }
  return postalCodes
}

export const getPostalCodes = (postalCodes: string[]): Promise<IGeoShapePostalCode[]> =>
  shapeRPO.getPostalCodes(postalCodes)

export const getPostalCodesWithOnesEndingByAStar = async (postalCodes: string[]): Promise<IGeoShapePostalCode[]> => {
  const normalPostalCodeIds = postalCodes.filter(p => !p.endsWith("*"))
  const normalPostalCodes: IGeoShapePostalCode[] =
    normalPostalCodeIds.length === 0 ? [] : await getPostalCodes(postalCodes.filter(p => !p.endsWith("*")))
  const endWithStarPostalCodes: IGeoShapePostalCode[] = []
  const endWithStarPostalCodeIds: string[] = postalCodes.filter(p => p.endsWith("*"))
  for (const endWithStarPostalCodeId of endWithStarPostalCodeIds) {
    const pcs = await searchPostalCodes(endWithStarPostalCodeId.substring(0, endWithStarPostalCodeId.length - 1), true)
    if (pcs.length > 0) {
      endWithStarPostalCodes.push(pcs[0])
    } else {
      console.error(`Unable to get postal code "${endWithStarPostalCodeId}"`)
    }
  }
  return [...normalPostalCodes, ...endWithStarPostalCodes]
}

export const searchStates = (filter: string): Promise<IGeoShapeState[]> => shapeRPO.searchStates(filter)

export const getStatesByIds = (ids: string[]): Promise<IGeoShapeState[]> => shapeRPO.getStatesById(ids)

export const searchCountries = (filter: string): Promise<IGeoShapeCountry[]> => shapeRPO.searchCountries(filter)

export const getCountriesById = (ids: string[]): Promise<IGeoShapeCountry[]> => shapeRPO.getCountriesById(ids)

export const getShapeTitle = ({ shape, withType }: IGetShapeTitleParams): string => {
  switch (shape.type) {
    case SHAPE_TYPES.POSTAL_CODE: {
      const title = getPostalCodeName(shape as IGeoShapePostalCode)
      return withType ? `${title} - ${translate("campaign.geo-targets.shape.postal-code.title")}` : title
    }
    case SHAPE_TYPES.CITY: {
      const city: IGeoShapeCity = shape as IGeoShapeCity
      const title = getCityName(city.name, city.country_id, city.state_id)
      return withType ? `${title} - ${translate("campaign.geo-targets.shape.city.title")}` : title
    }
    case SHAPE_TYPES.STATE: {
      const title = getStateName(shape as IGeoShapeState)
      return withType ? `${title} - ${translate("campaign.geo-targets.shape.state.title")}` : title
    }
    case SHAPE_TYPES.COUNTRY: {
      const country: IGeoShapeCountry = shape as IGeoShapeCountry
      const title = getCountryName(country)
      return withType ? `${title} - ${translate("campaign.geo-targets.shape.country.title")}` : title
    }
    case SHAPE_TYPES.METROPOLITAN: {
      const metropolitan: IGeoShapeMetropolitan = shape as IGeoShapeMetropolitan
      const title = translate(metropolitan.labelKey, metropolitan.cityIds.length)
      return withType ? `${title} - ${translate("campaign.geo-targets.shape.metropolitan.title")}` : title
    }
  }
}

export const getCountryName = (country: IGeoShapeCountry): string => `${country.name} (${country.country_shape_id})`

export const getStateName = (state: IGeoShapeState): string =>
  `${state.name} (${state.state_shape_id}) - ${state.country_id}`

export const getPostalCodeName = (postalCodeShape: IGeoShapePostalCode): string => {
  const postalCode =
    postalCodeShape.postal_code_shape_ids.length > 1
      ? translate("campaign.geo-targets.shape.postal-code.multiple.title", postalCodeShape.postal_code)
      : postalCodeShape.postal_code
  return `${postalCode} - ${
    postalCodeShape.country_id || translate("campaign.geo-targets.shape.postal-code.not.found")
  }`
}

export const getCityName = (name: string, country: string, state: string): string => {
  state = state ? ` (${state})` : ""
  country = country ? ` - ${country}` : ""
  return `${name}${state}${country}`
}

export const getShapeId = (shape: IGeoShape): string => {
  switch (shape.type) {
    case SHAPE_TYPES.POSTAL_CODE: {
      return (shape as IGeoShapePostalCode).postal_code
    }
    case SHAPE_TYPES.CITY: {
      return (shape as IGeoShapeCity).external_id
    }
    case SHAPE_TYPES.STATE: {
      return (shape as IGeoShapeState).state_shape_id
    }
    case SHAPE_TYPES.COUNTRY: {
      return (shape as IGeoShapeCountry).country_shape_id
    }
    case SHAPE_TYPES.METROPOLITAN: {
      return (shape as IGeoShapeMetropolitan).shape_id
    }
  }
}

export function sortShapeType(type1: SHAPE_TYPES, type2: SHAPE_TYPES): number {
  const difference = getTypeSortWeight(type1) - getTypeSortWeight(type2)
  if (difference < 0) {
    return -1
  } else if (difference === 0) {
    return 0
  } else {
    return 1
  }
}

export async function fetchAndSetShapeGeometry(shape: IGeoShape): Promise<IGeoShape> {
  switch (shape.type) {
    case SHAPE_TYPES.CITY: {
      const cityShape = shape as IGeoShapeCity
      return {
        ...cityShape,
        ...(await shapeRPO.getCityShapeData(cityShape.city_shape_id))
      }
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      const postalCodeShape = shape as IGeoShapePostalCode
      const postalCodeData = await shapeRPO.getPostalCodeData(postalCodeShape.postal_code_shape_ids, shape)
      return {
        ...postalCodeShape,
        ...postalCodeData
      } as IGeoShapePostalCode
    }
    case SHAPE_TYPES.STATE: {
      const stateShape = shape as IGeoShapeState
      return {
        ...stateShape,
        ...(await shapeRPO.getStateData(stateShape.state_shape_id))
      }
    }
    case SHAPE_TYPES.COUNTRY: {
      const countryShape = shape as IGeoShapeCountry
      return {
        ...countryShape,
        ...(await shapeRPO.getCountryData(countryShape.country_shape_id))
      }
    }
    case SHAPE_TYPES.METROPOLITAN: {
      const metropolitanShape = shape as IGeoShapeMetropolitan
      return await getMetropolitanWithShape(metropolitanShape)
    }
  }
}

function getTypeSortWeight(type: SHAPE_TYPES): number {
  switch (type) {
    case SHAPE_TYPES.CITY: {
      return 0
    }
    case SHAPE_TYPES.METROPOLITAN: {
      return 1
    }
    case SHAPE_TYPES.POSTAL_CODE: {
      return 2
    }
    case SHAPE_TYPES.STATE: {
      return 3
    }
    case SHAPE_TYPES.COUNTRY: {
      return 4
    }
  }
}
